import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import queryString from 'query-string'
import { useCallback } from 'react'
import {
  AutomationRuleCondition,
  AutomationRuleDetailModel,
  AutomationRuleDetailResponse,
  AutomationRuleFundingSource,
  AutomationRuleListModel,
  AutomationRuleListResponse,
  AutomationRuleStatusType,
} from 'src/models/automationRules.models'
import { PaginationResponse } from 'src/models/pagination.models'
import { automationRulesQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getNextExtendPageParam,
  MutationMethod,
  useExtendInfiniteQuery,
  useExtendMutation,
  UseExtendMutationVariablesBody,
  useExtendQuery,
} from 'src/queries/queries.utils'

export type PostAutomationRuleRequest = {
  name: string
  conditions: AutomationRuleCondition[]
  mappings: Array<{
    categoryId: string
    labelId: string
  }>
}

export type PutAutomationRuleRequest = PostAutomationRuleRequest & {
  status: AutomationRuleStatusType
}

// TODO: backend doesn't allow patching of conditions
export type PatchAutomationRuleRequest = Omit<
  Partial<PutAutomationRuleRequest>,
  'conditions'
>

export const usePostAutomationRule = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AutomationRuleCondition,
    UseExtendMutationVariablesBody<PostAutomationRuleRequest>
  >(MutationMethod.POST, {
    mutationUrl: () => `/rules`,
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    },
  })
}

export interface PutAutomationRuleVariables {
  id: string
  body: PutAutomationRuleRequest
}

export const usePutAutomationRule = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AutomationRuleListResponse,
    PutAutomationRuleVariables
  >(MutationMethod.PUT, {
    mutationUrl: ({ id }) => `/rules/${id}`,
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    },
  })
}

export interface PatchAutomationRuleVariables {
  id: string
  body: PatchAutomationRuleRequest
}

export const usePatchAutomationRule = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AutomationRuleListResponse,
    PatchAutomationRuleVariables
  >(MutationMethod.PATCH, {
    mutationUrl: ({ id }) => `/rules/${id}`,
    mutationKey: automationRulesQueryKeys.patch(),
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: automationRulesQueryKeys.all(),
      })
    },
  })
}

interface QueryAutomationRuleDetailOptions
  extends ExtendBaseQueryOptions<
    AutomationRuleDetailResponse,
    AutomationRuleDetailModel
  > {
  ruleId: string | undefined
}

export const useAutomationRuleDetail = ({
  ruleId,
  enabled = true,
  ...rest
}: QueryAutomationRuleDetailOptions) => {
  return useExtendQuery({
    queryKey: automationRulesQueryKeys.detail(ruleId),
    queryUrl: () => `/rules/${ruleId}/details`,
    enabled: enabled && ruleId !== undefined,
    select: (data) => new AutomationRuleDetailModel(data),
    ...rest,
  })
}

interface GetAutomationRuleListResponse {
  rules: AutomationRuleListResponse[]
  pagination: PaginationResponse
}

export enum AutomationRuleListSortField {
  // use these with SortDirection
  Name = 'name',
  CreatedAt = 'createdAt',
}

interface GetAutomationRuleListFilters {
  search?: string
  status?: AutomationRuleStatusType[]
  sortField?: AutomationRuleListSortField
  sortDirection?: 'ASC' | 'DESC'
  fundingSources?: AutomationRuleFundingSource[]
  fundingSourceIds?: string[]
}

interface QueryAutomationRulesOption
  extends ExtendBaseInfiniteQueryOptions<
    GetAutomationRuleListResponse,
    number,
    AutomationRuleListModel[]
  > {
  filters: GetAutomationRuleListFilters
}

export const useAutomationRuleList = ({
  filters,
  ...rest
}: QueryAutomationRulesOption) => {
  return useExtendInfiniteQuery({
    queryKey: automationRulesQueryKeys.all(),
    queryUrl: ({ pageParam }) =>
      `/rules?${queryString.stringify({ page: pageParam, ...filters })}`,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
    select: useCallback(
      (data: InfiniteData<GetAutomationRuleListResponse, number>) =>
        data.pages
          .flatMap((page) => page.rules)
          .map((rule) => new AutomationRuleListModel(rule)),
      [],
    ),
    ...rest,
  })
}
